import type { BreadcrumbCollapseLevel } from '@/lib/breadcrumbLabels';
import { useLayoutEffect, useRef, useState, type RefObject } from 'react';

const MAX_COLLAPSE_LEVEL: BreadcrumbCollapseLevel = 2;

function hasOverflow(container: HTMLElement, list: HTMLElement): boolean {
  return list.scrollWidth > container.clientWidth + 1;
}

function nextCollapseLevel(
  prev: BreadcrumbCollapseLevel,
  itemCount: number
): BreadcrumbCollapseLevel {
  if (prev >= MAX_COLLAPSE_LEVEL) return prev;
  if (prev === 0) return itemCount >= 3 ? 1 : 2;
  return 2;
}

/**
 * Progressively collapses breadcrumb labels until the trail fits the container.
 * Only grows the collapse level while overflowing; never shrinks on the same
 * measure (shrinking after a fit re-introduces overflow and infinite updates).
 * When the container widens, collapse resets to 0 and re-grows if still needed.
 */
export function useBreadcrumbCollapseLevel(
  containerRef: RefObject<HTMLElement | null>,
  listRef: RefObject<HTMLElement | null>,
  itemCount: number
): BreadcrumbCollapseLevel {
  const [collapseLevel, setCollapseLevel] = useState<BreadcrumbCollapseLevel>(0);
  const itemCountRef = useRef(itemCount);
  itemCountRef.current = itemCount;

  useLayoutEffect(() => {
    setCollapseLevel(0);
  }, [itemCount]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const list = listRef.current;
    if (!container || !list) return;

    const growIfNeeded = () => {
      setCollapseLevel((prev) => {
        if (hasOverflow(container, list)) {
          return nextCollapseLevel(prev, itemCountRef.current);
        }
        return prev;
      });
    };

    growIfNeeded();

    let previousWidth = container.clientWidth;
    const observer = new ResizeObserver(() => {
      const width = container.clientWidth;
      if (width > previousWidth) {
        // Room to show more label — re-measure from level 0.
        setCollapseLevel(0);
      } else if (width < previousWidth) {
        growIfNeeded();
      }
      previousWidth = width;
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [containerRef, listRef, itemCount, collapseLevel]);

  return collapseLevel;
}
